import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "ticketino-api-client";
import { getCountries, UpdateBuyerAddress, GetOrganizerUser } from "../utils/api";
import { useLanguage } from "../hooks/useLanguage";
import { useOrder } from "../hooks/useOrder";
import { toZonedTime } from 'date-fns-tz';

import Header from "./components/Header";
import Footer from "./components/Footer";
import Loading from "./components/Loading";

import previewSvg from "../images/icons/eye.svg";

const Registration = () => {
    const [regularToken, setRegularToken] = useState(null);

    const [formError, setFormError] = useState("");

    const [resources, setResources] = useState({});
    const [countries, setCountries] = useState([]);

    const { language } = useLanguage();

    const [buyerAddress, setBuyerAddress] = useState({
        organizerId: 0,
        salutation: "",
        firstName: "",
        name: "",
        company: "",
        street: "",
        city: "",
        postCode: "",
        countryId: 176,
        email: "",
        code11: "",
        password: "",
        newPassword: "",
        confirmpassword: "",
    });

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const [isAgbChecked, setIsAgbChecked] = useState(false);

    const timeZone = 'Europe/Zurich';

    let navigate = useNavigate();
    let baseUrl = process.env.REACT_APP_BASEURL_API;
    api.defaults.baseURL = baseUrl;

    const order = useOrder(baseUrl);

    useEffect(() => {
        setBuyerAddress(prev => ({ ...prev, email: localStorage.getItem("existingUsername") }));
        setRegularToken(localStorage.getItem("token"));
    }, []);

    useEffect(() => {
        if (language) {
            requestResources();
        }
    }, [language]);

    useEffect(() => {
        if (regularToken && language) {
            loadCountries();
        }
    }, [regularToken, language]);

    const requestResources = async () => {
        await axios
            .get(`form/resources/${language}`)
            .then((res) => {
                setResources(res.data?.translation);
            })
            .catch((error) => console.error(error.response.data));
    };

    const loadCountries = async () => {
        try {
            if (!regularToken) {
                throw new Error("No regular token found at LoadCountries");
            }
            let countries = await getCountries(baseUrl, "token", regularToken, language);
            const indexCH = countries.findIndex(c => c.id === 176);
            const ch = countries[indexCH];
            countries.splice(indexCH, 1);
            var indexDE = countries.findIndex(c => c.id === 229);
            const de = countries[indexDE];
            countries.splice(indexDE, 1);
            var indexAU = countries.findIndex(c => c.id === 228);
            const au = countries[indexAU];
            countries.splice(indexAU, 1);
            var indexFR = countries.findIndex(c => c.id === 59);
            const fr = countries[indexFR];
            countries.splice(indexFR, 1);
            var indexIT = countries.findIndex(c => c.id === 87);
            const it = countries[indexIT];
            countries.splice(indexIT, 1);
            countries.unshift({ id: 0, name: "---------" });
            countries.unshift(it);
            countries.unshift(au);
            countries.unshift(fr);
            countries.unshift(de);
            countries.unshift(ch);
            setCountries(countries);
        }
        catch (error) {
            console.error(error);
        }
    }

    const validateInputFields = () => {
        const genericError = resources?.ErrorPleaseFillMandatoryFields;

        if (!buyerAddress) return { valid: false, error: null };
        if (!buyerAddress.firstName || buyerAddress.firstName.length > 70) return { valid: false, error: genericError };
        if (!buyerAddress.name || buyerAddress.name.length > 70) return { valid: false, error: genericError };
        if (!buyerAddress.street || buyerAddress.street.length > 70) return { valid: false, error: genericError };
        if (!buyerAddress.postCode || buyerAddress.postCode.length > 70) return { valid: false, error: genericError };
        if (!buyerAddress.city || buyerAddress.city.length > 70) return { valid: false, error: genericError };
        if (!buyerAddress.code11 || buyerAddress.code11.length > 100) return { valid: false, error: genericError };
        if (!buyerAddress.countryId) return { valid: false, error: genericError };
        if (!buyerAddress.email || !/^\S+@\S+\.\S+$/.test(buyerAddress.email)) return { valid: false, error: resources?.ErrorEmailNotValid };
        if (!buyerAddress.password) return { valid: false, error: resources?.ErrorPasswordMismatch };
        if (buyerAddress.password !== buyerAddress.confirmpassword) return { valid: false, error: resources?.ErrorPasswordMismatch };
        /*if (!isAgbChecked) return { valid: false, error: resources?.ErrorPleaseAcceptAGB };*/
        return { valid: true };
    }

    const handleUpdateBuyerAddress = async (e) => {
        if (e) e.preventDefault();
        setFormError("");
        const validation = validateInputFields();
        if (!validation.valid) {
            setFormError(validation.error || "Please fill all mandatory fields correctly.");
            return;
        }
        if (!order?.tickets) {
            setFormError(resources?._SessionExpired);
            return;
        }

        try {
            buyerAddress.newPassword = buyerAddress.password;
            buyerAddress.copyNewPassword = buyerAddress.confirmpassword;

            // Clear any old buyerToken before starting
            localStorage.removeItem("buyerToken");
            localStorage.removeItem("loggedInOrganizerId");

            await UpdateBuyerAddress(baseUrl, "token", regularToken, order.id, buyerAddress);

            // Add a small delay to ensure account is fully created on the backend
            await new Promise(resolve => setTimeout(resolve, 1000));

            var dateTimeNow = new Date();
            var zurichDateTimeNow = toZonedTime(dateTimeNow, timeZone)

            localStorage.setItem("loginDateTime", zurichDateTimeNow.toISOString()); // Save login datetime

            // Log in the user after successful registration to get buyerToken
            const loginRes = await axios.post('/form/login', {
                username: buyerAddress.email,
                password: buyerAddress.password
            });

            // Store the buyerToken BEFORE calling fetchOrganizerUser
            if (!loginRes.data || loginRes.data === "Error") {
                setFormError("Login failed after registration. Please try logging in manually.");
                return;
            }

            localStorage.setItem("buyerToken", loginRes.data); // Store the buyerToken
            localStorage.setItem("username", buyerAddress.email); // Save username in session

            // Now fetch the organizer ID
            const loggedInOrganizerId = await fetchOrganizerUser(loginRes.data, buyerAddress.email)

            if (!loggedInOrganizerId) {
                console.error('Failed to fetch organizer ID. Login response:', loginRes.data);
                console.error('Email used:', buyerAddress.email);
                setFormError(resources?.Account_RegistrationAuthenticationFailed || "Authentication failed. Please try again or contact support.");
                return;
            }

            localStorage.setItem("loggedInOrganizerId", loggedInOrganizerId);

            navigate(`/${language}/information`);
        } catch (error) {
            console.error('Registration error:', error);
            setFormError(resources?._Save + " failed. " + (error.message || ""));
        }
    };

    const fetchOrganizerUser = async (buyerToken, username) => {
        try {
            if (!buyerToken) {
                throw new Error("No buyerToken token found at fetchOrganizerUser");
            }         
            
            const data = await GetOrganizerUser(baseUrl, "buyerToken", buyerToken, username);
                        
            if (!Array.isArray(data)) {
                console.error('GetOrganizerUser did not return an array:', data);
                return null;
            }
            
            const matchedOrganizer = data.find(org => 
                org.username?.toLowerCase().trim() === username.toLowerCase().trim()
            );

            if (!matchedOrganizer) {
                console.error('No matching organizer found. Available organizers:', 
                    data.map(org => ({ id: org.id, username: org.username })));
            }

            return matchedOrganizer?.id;
        } catch (error) {
            console.error('Error fetching organizer user:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            });
            return null;
        }
    };

    const onInputChange = (e) => {
        setBuyerAddress({ ...buyerAddress, [e.target.name]: e.target.value });
    };

    return (
        <>
            <Header language={language} pagePath="login" pageTitle={resources?.RegistrationTitle}
                ticketCount={order?.tickets?.length || 0} />

            <div className="page-container">

                <div className="container wrapper">

                    {/* info */}
                    <div className="row mb-4">
                        <div className="col-md-12 text-center">
                            <span dangerouslySetInnerHTML={{ __html: resources?.Account_RegistrationDesc }}></span>
                        </div>
                    </div>

                    {/* navigation */}
                    {/*<div className="row mb-5">*/}
                    {/*    <div className="col-6">*/}
                    {/*        <button className="btn-custom btn-navigation" type="submit">*/}
                    {/*            {resources?.Account_LoginRegister}*/}
                    {/*        </button>*/}
                    {/*    </div>*/}
                    {/*    <div className="col-6 text-end">*/}
                    {/*        <button className="btn-custom transparent btn-navigation" type="submit"*/}
                    {/*            onClick={() => navigate(`/${language}/login`)}*/}
                    {/*        >*/}
                    {/*            {resources?.Account_LoginLogin}*/}
                    {/*        </button>*/}
                    {/*    </div>*/}
                    {/*</div>*/}

                    {/* inputs */}
                    <div className="form-group row">
                        <label htmlFor="salutation" className="col-md-3 col-form-label">{resources?.BuyerSalutation}</label>
                        <div className="col-md-9">
                            <select className="form-select" value={buyerAddress?.salutation || ""} name="salutation" onChange={onInputChange}>
                                <option value=""></option>
                                <option value={resources?.BuyerSalutationMr}>{resources?.BuyerSalutationMr}</option>
                                <option value={resources?.BuyerSalutationMs}>{resources?.BuyerSalutationMs}</option>
                                {language === "en" && (
                                    <option value="Mrs.">Mrs.</option>
                                )}
                                <option value={resources?.BuyerSalutationDiverse}>{resources?.BuyerSalutationDiverse}</option>
                            </select>
                        </div>
                    </div>

                    <div className="form-group row">
                        <label htmlFor="firstName" className="col-md-3 col-form-label">{resources?.BuyerFirstname} *</label>
                        <div className="col-md-9">
                            <input
                                type="text"
                                name="firstName"
                                className="form-control"
                                value={buyerAddress?.firstName || ""}
                                onChange={onInputChange}
                            />
                        </div>
                    </div>

                    <div className="form-group row">
                        <label htmlFor="name" className="col-md-3 col-form-label">{resources?.BuyerLastname} *</label>
                        <div className="col-md-9">
                            <input
                                type="text"
                                name="name"
                                className="form-control"
                                value={buyerAddress?.name || ""}
                                onChange={onInputChange}
                            />
                        </div>
                    </div>

                    <div className="form-group row">
                        <label htmlFor="company" className="col-md-3 col-form-label">{resources?.BuyerCompanySchool}</label>
                        <div className="col-md-9">
                            <input
                                type="text"
                                name="company"
                                className="form-control"
                                value={buyerAddress?.company || ""}
                                onChange={onInputChange}
                            />
                        </div>
                    </div>

                    <div className="form-group row">
                        <label htmlFor="street" className="col-md-3 col-form-label">{resources?.BuyerStreet} *</label>
                        <div className="col-md-9">
                            <input
                                type="text"
                                name="street"
                                className="form-control"
                                value={buyerAddress?.street || ""}
                                onChange={onInputChange}
                            />
                        </div>
                    </div>

                    <div className="form-group row">
                        <label htmlFor="postCode" className="col-md-3 col-form-label">{resources?.BuyerPostcode} *</label>
                        <div className="col-md-9">
                            <input
                                type="text"
                                name="postCode"
                                className="form-control"
                                value={buyerAddress?.postCode || ""}
                                onChange={onInputChange}
                            />
                        </div>
                    </div>

                    <div className="form-group row">
                        <label htmlFor="city" className="col-md-3 col-form-label">{resources?.BuyerCity} *</label>
                        <div className="col-md-9">
                            <input
                                type="text"
                                name="city"
                                className="form-control"
                                value={buyerAddress?.city || ""}
                                onChange={onInputChange}
                            />
                        </div>
                    </div>

                    <div className="form-group row">
                        <label htmlFor="countryId" className="col-md-3 col-form-label">{resources?.BuyerCountry} *</label>
                        <div className="col-md-9">
                            <select
                                className="form-select"
                                aria-label=""
                                id="countryId"
                                name="countryId"
                                value={buyerAddress?.countryId || 176}
                                onChange={onInputChange}
                            >
                                {
                                    countries.map((country) => (
                                        <option key={country.id} value={country.id}>{country.name}</option>
                                    ))
                                }
                            </select>
                        </div>
                    </div>

                    {/*<div className="form-group row">*/}
                    {/*    <label htmlFor="email" className="col-md-3 col-form-label">{resources?.BuyerEmail} *</label>*/}
                    {/*    <div className="col-md-9">*/}
                    {/*        <input*/}
                    {/*            type="text"*/}
                    {/*            name="email"*/}
                    {/*            className="form-control"*/}
                    {/*            value={buyerAddress?.email || ""}*/}
                    {/*            onChange={onInputChange}*/}
                    {/*        />*/}
                    {/*    </div>*/}
                    {/*</div>*/}

                    <div className="form-group row">
                        <label htmlFor="countryId" className="col-md-3 col-form-label">{resources?.BuyerWhereDidYouHear} *</label>
                        <div className="col-md-9">
                            <select
                                className="form-select"
                                aria-label=""
                                id="code11"
                                name="code11"
                                value={buyerAddress?.code11 || ""}
                                onChange={onInputChange}
                            >
                                <option value=""></option>
                                {language === "de" ? (
                                    <>
                                        <option value="Radio, Podcast">Radio, Podcast</option>
                                        <option value="Online-Werbung (Banner, Google Ads)">Online-Werbung (Banner, Google Ads)</option>
                                        <option value="Social Media (Instagram, Facebook, TikTok, etc.)">Social Media (Instagram, Facebook, TikTok, etc.)</option>
                                        <option value="Newsletter, E-Mail">Newsletter, E-Mail</option>
                                        <option value="Plakat, Flyer, Printwerbung">Plakat, Flyer, Printwerbung</option>
                                        <option value="Empfehlung von Freunden">Empfehlung von Freunden</option>
                                        <option value="Google, Suchmaschine">Google, Suchmaschine</option>
                                        <option value="KI Suche (ChatGPT, Gemini, etc.)">KI Suche (ChatGPT, Gemini, etc.)</option>
                                        <option value="Messe, Event, Vortrag">Messe, Event, Vortrag</option>
                                        <option value="Zeitung, Presse">Zeitung, Presse</option>
                                        <option value="Ich war schon mal da, Wiederholungsgast">Ich war schon mal da, Wiederholungsgast</option>
                                        <option value="Wettbewerb, Gewinnspiel">Wettbewerb, Gewinnspiel</option>
                                        <option value="Booking-Plattform (GetYourGuide, TripAdvisor, etc.)">Booking-Plattform (GetYourGuide, TripAdvisor, etc.)</option>
                                        <option value="Weiss nicht, Kann mich nicht erinnern">Weiss nicht, Kann mich nicht erinnern</option>
                                    </>
                                ) : (
                                    <>
                                        <option value="Radio, Podcast">Radio, Podcast</option>
                                        <option value="Online Advertising (Banner, Google Ads)">Online Advertising (Banner, Google Ads)</option>
                                        <option value="Social Media (Instagram, Facebook, TikTok, etc.)">Social Media (Instagram, Facebook, TikTok, etc.)</option>
                                        <option value="Newsletter, Email">Newsletter, Email</option>
                                        <option value="Poster, Flyer, Print Advertising">Poster, Flyer, Print Advertising</option>
                                        <option value="Recommendation from Friends">Recommendation from Friends</option>
                                        <option value="Google, Search Engine">Google, Search Engine</option>
                                        <option value="AI Search (ChatGPT, Gemini, etc.)">AI Search (ChatGPT, Gemini, etc.)</option>
                                        <option value="Fair, Event, Presentation">Fair, Event, Presentation</option>
                                        <option value="Newspaper, Press">Newspaper, Press</option>
                                        <option value="I've been here before, Return Guest">I've been here before, Return Guest</option>
                                        <option value="Competition, Raffle">Competition, Raffle</option>
                                        <option value="Booking Platform (GetYourGuide, TripAdvisor, etc.)">Booking Platform (GetYourGuide, TripAdvisor, etc.)</option>
                                        <option value="Don't know, Can't remember">Don't know, Can't remember</option>
                                    </>
                                )}
                            </select>
                        </div>
                    </div>

                    <div className="form-group row">
                        <label htmlFor="password" className="col-sm-3 col-form-label">{resources?.BuyerPassword} *</label>
                        <div className="col-sm-9 position-relative">
                            <div className="password-input-wrapper">
                                <input
                                    className="form-control"
                                    name="password"
                                    type={showPassword ? "text" : "password"}
                                    value={buyerAddress?.password || ""}
                                    onChange={onInputChange}
                                />
                                <img
                                    src={previewSvg}
                                    alt="Show password"
                                    className="password-toggle-eye"
                                    onClick={() => setShowPassword((v) => !v)}
                                    style={{ cursor: 'pointer' }}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="form-group row">
                        <label htmlFor="confirmpassword" className="col-sm-3 col-form-label">{resources?.BuyerConfirmPassword} *</label>
                        <div className="col-sm-9 position-relative">
                            <div className="password-input-wrapper">
                                <input
                                    className="form-control"
                                    name="confirmpassword"
                                    type={showConfirmPassword ? "text" : "password"}
                                    value={buyerAddress?.confirmpassword || ""}
                                    onChange={onInputChange}
                                />
                                <img
                                    src={previewSvg}
                                    alt="Show confirm password"
                                    className="password-toggle-eye"
                                    onClick={() => setShowConfirmPassword((v) => !v)}
                                    style={{ cursor: 'pointer' }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* AGBs */}
                    {/*<div className="mt-5">*/}
                    {/*    */}{/* Combined AGB */}
                    {/*    <div className="form-check">*/}
                    {/*        <input*/}
                    {/*            id="agb"*/}
                    {/*            className="form-check-input"*/}
                    {/*            type="checkbox"*/}
                    {/*            checked={isAgbChecked}*/}
                    {/*            onChange={e => setIsAgbChecked(e.target.checked)}*/}
                    {/*        />*/}
                    {/*        <label*/}
                    {/*            className="d-inline"*/}
                    {/*            htmlFor="agb"*/}
                    {/*        >*/}
                    {/*            <div>*/}
                    {/*                <div dangerouslySetInnerHTML={{ __html: resources?.Account_RegistrationAGB1 }}></div>*/}
                    {/*                <div className="mt-2" dangerouslySetInnerHTML={{ __html: resources?.Account_RegistrationAGB2 }}></div>*/}
                    {/*            </div>*/}
                    {/*        </label>*/}
                    {/*    </div>*/}
                    {/*</div>*/}

                    {/* infos */}
                    <div className="row mt-5 text-center">
                        <div className="col-md-12 d-flex flex-column">
                            <span><b>{resources?.Account_RegistrationAlreadyRegistered}</b></span>
                            <span>
                                {resources?._Click}
                                <button
                                    type="button"
                                    style={{ background: "none", border: "none", color: "inherit", textDecoration: "underline", cursor: "pointer" }}
                                    onClick={() => navigate(`/${language}/username`)}
                                >
                                    {resources?._Here}
                                </button>
                                {resources?.Account_LoginAlreadyHasAccountDesc}
                            </span>
                        </div>
                    </div>


                    {/* button */}
                    <div className="row mt-5">
                        <div className="col-md-12 text-center">
                            <button
                                className="btn-custom btn-navigation"
                                type="button"
                                onClick={handleUpdateBuyerAddress}
                            >
                                {resources?.Account_RegistrationCreateAccount}
                            </button>
                        </div>
                    </div>

                    {formError &&
                        <div className="row text-start mt-2">
                            <p className="text-danger fw-bold">{formError}</p>
                        </div>
                    }

                </div>

            </div>

            <Footer language={language} />
        </>
    )
}

export default Registration;